import { useAppDispatch } from '../../hooks/redux';
import {
  AssignAiSystemToUserRequest,
  assignAiSystemToUserWithoutInvitation,
  deleteInvitation,
  deleteUserFromAccount,
  deleteUserFromSingleAiSystem,
  getAiSystemsForUser,
  getInvitedTeamMembers,
  getRegisteredTeamMembers,
  getTeamRoles,
  getTeamUsers,
  resendInvitation,
  searchUsers,
  updateUserRoleInAiSystem
} from '../../services/TeamService';
import {
  assignAiSystemWithoutInvitationFailure,
  assignAiSystemWithoutInvitationStart,
  fetchAiSystemsForUserError,
  fetchAiSystemsForUserStart,
  fetchAiSystemsForUserSuccess,
  fetchDeleteUserFromAccountFailure,
  fetchDeleteUserFromAccountStart,
  fetchDeleteUserFromAccountSuccess,
  fetchTeamInvitationsFailure,
  fetchTeamInvitationsStart,
  fetchTeamInvitationsSuccess,
  fetchTeamMembersFailure,
  fetchTeamMembersStart,
  fetchTeamMembersSuccess,
  fetchTeamRolesFailure,
  fetchTeamRolesStart,
  fetchTeamRolesSuccess,
  fetchTeamUsersFailure,
  fetchTeamUsersStart,
  fetchTeamUsersSuccess,
  removedLastSystemPerUserFailure,
  removedLastSystemPerUserStart,
  removedLastSystemPerUserSuccess,
  resendInvitationFailure,
  resendInvitationStart,
  resendInvitationSuccess,
  setAssignAiSystemWithoutInvitationSuccess,
  setIsRolePerAiSystemChangedError,
  setIsRolePerAiSystemChangedStart,
  setIsRolePerAiSystemChangedSuccess,
  setRemoveUserFromSingleAiSystemError,
  setRemoveUserFromSingleAiSystemLoading,
  setRemoveUserFromSingleAiSystemSuccess
} from '../../store/slices/userManagementSlice';
import { TeamRolesDTO, UpdateUserRoleRequest } from '../../types/Team.types';

export function useUserManagement() {
  const dispatch = useAppDispatch();

  const fetchTeamInvitations = async (accountId: string) => {
    dispatch(fetchTeamInvitationsStart());
    try {
      const response = await getInvitedTeamMembers(accountId);
      dispatch(fetchTeamInvitationsSuccess(response.data));
    } catch (error) {
      if (error instanceof Error) {
        dispatch(fetchTeamInvitationsFailure(error.message));
      }
    }
  };

  const fetchTeamMembers = async (accountId: string, sort?: 'asc' | 'desc') => {
    dispatch(fetchTeamMembersStart());
    try {
      const response = await getRegisteredTeamMembers(accountId, sort);
      dispatch(fetchTeamMembersSuccess(response.data));
    } catch (error) {
      if (error instanceof Error) {
        dispatch(fetchTeamMembersFailure(error.message));
      }
    }
  };

  const fetchTeamUsers = async () => {
    dispatch(fetchTeamUsersStart());
    try {
      const response = await getTeamUsers();
      dispatch(fetchTeamUsersSuccess(response.data));
    } catch (error) {
      if (error instanceof Error) {
        dispatch(fetchTeamUsersFailure(error.message));
      }
    }
  };

  const fetchTeamRoles = async () => {
    dispatch(fetchTeamRolesStart());
    try {
      const response = await getTeamRoles();
      dispatch(fetchTeamRolesSuccess(response.data as TeamRolesDTO));
    } catch (error) {
      if (error instanceof Error) {
        dispatch(fetchTeamRolesFailure(error.message));
      }
    }
  };

  const deleteTeamInvitation = async (
    aiSystemId: string,
    invitationId: string
  ) => {
    console.log('Deleting team invitation for AI System ID:', aiSystemId);
    try {
      const response = await deleteInvitation(aiSystemId, invitationId);
      dispatch(fetchTeamInvitationsSuccess(response.data));
    } catch (error) {
      if (error instanceof Error) {
        dispatch(fetchTeamInvitationsFailure(error.message));
      }
    }
  };

  const searchTeamMembers = async (accountId: string, query: string) => {
    console.log('Searching team members for Account ID:', accountId);
    try {
      const response = await searchUsers(accountId, query);
      //handle response if needed
    } catch (error) {
      if (error instanceof Error) {
        //handle error if needed
      }
    }
  };

  const fetchAiSystemsForUser = async (accountId: string, userId: string) => {
    dispatch(fetchAiSystemsForUserStart(true));
    try {
      const response = await getAiSystemsForUser(accountId, userId);
      dispatch(fetchAiSystemsForUserSuccess(response.data));
    } catch (error) {
      if (error instanceof Error) {
        dispatch(fetchAiSystemsForUserError(error.message));
      }
    }
  };

  const sendRoleChangesPerAiSystem = async (
    aiSystemId: string,
    userId: string,
    bodyRequest: UpdateUserRoleRequest
  ) => {
    dispatch(setIsRolePerAiSystemChangedStart(true));
    try {
      await updateUserRoleInAiSystem(aiSystemId, userId, bodyRequest);

      dispatch(setIsRolePerAiSystemChangedSuccess(true));
    } catch (error) {
      if (error instanceof Error) {
        dispatch(setIsRolePerAiSystemChangedError(error.message));
      }
    }
  };

  const removeUserFromSingleAiSystem = async (
    accountId: string,
    userId: string,
    aiSystemId: string,
    lastAiSystem: boolean = false
  ) => {
    dispatch(setRemoveUserFromSingleAiSystemLoading(true));
    if (lastAiSystem) {
      dispatch(removedLastSystemPerUserStart());
    }
    try {
      await deleteUserFromSingleAiSystem(accountId, userId, aiSystemId);
      dispatch(setRemoveUserFromSingleAiSystemSuccess(true));
      if (lastAiSystem) {
        dispatch(removedLastSystemPerUserSuccess());
      }
    } catch (error) {
      if (error instanceof Error) {
        dispatch(setRemoveUserFromSingleAiSystemError(error.message));
        if (lastAiSystem) {
          dispatch(removedLastSystemPerUserFailure(error.message));
        }
      }
    }
  };

  const deleteTeamMemberFromAccount = async (
    accountId: string,
    userId: string
  ) => {
    dispatch(fetchDeleteUserFromAccountStart());
    try {
      await deleteUserFromAccount(accountId, userId);
      dispatch(fetchDeleteUserFromAccountSuccess(true));
    } catch (error) {
      if (error instanceof Error) {
        dispatch(fetchDeleteUserFromAccountFailure(error.message));
      }
    }
  };

  const resendInvitationForMember = async (
    accountId: string,
    invitationId: string
  ) => {
    dispatch(resendInvitationStart());
    try {
      await resendInvitation(accountId, invitationId);
      dispatch(resendInvitationSuccess());
    } catch (error) {
      if (error instanceof Error) {
        dispatch(resendInvitationFailure(error.message));
      }
    }
  };

  const assignAiSystemWithoutInvitation = async (
    accountId: string,
    params: AssignAiSystemToUserRequest
  ) => {
    dispatch(assignAiSystemWithoutInvitationStart());
    try {
      await assignAiSystemToUserWithoutInvitation(accountId, params);
      dispatch(setAssignAiSystemWithoutInvitationSuccess(true));
    } catch (error) {
      if (error instanceof Error) {
        dispatch(assignAiSystemWithoutInvitationFailure(error.message));
      }
    }
  };

  return {
    fetchTeamInvitations,
    fetchTeamMembers,
    fetchTeamUsers,
    fetchTeamRoles,
    deleteTeamInvitation,
    searchTeamMembers,
    fetchAiSystemsForUser,
    sendRoleChangesPerAiSystem,
    removeUserFromSingleAiSystem,
    deleteTeamMemberFromAccount,
    resendInvitationForMember,
    assignAiSystemWithoutInvitation
  };
}
